import { Financials, Peer, RankingDetail, Rating } from '@benzinga/stock-reports-manager';
import type { EChartsOption } from 'echarts';
import dayjs from 'dayjs';

export type StockReportRankings = RankingDetail & {
  symbol: string;
};

export type MappedPeer = {
  exchange: string;
  name: string;
  options: EChartsOption;
  symbol: string;
  rankings: StockReportRankings | null;
};

export type Section = {
  title?: string;
  type?: string;
  blurb?: string;
  description?: string;
  options?: EChartsOption;
  peers?: MappedPeer[];
  average_dollars?: number;
  change_percent?: number;
  max_dollars?: number;
  min_dollars?: number;
  num_ratings?: number;
  ratings?: Rating[];
  symbol?: string;
  callout?: string;
  pe_avg?: number;
  pe_multiple?: number;
  rank?: string;
  debt_to_equity?: number;
  debt_to_equity_options?: EChartsOption;
  operating_margin?: number;
  operating_margin_options?: EChartsOption;
  quick_ratio?: number;
  quick_ratio_options?: EChartsOption;
  first_year?: string;
  peer_sharpe?: number;
  sharpe?: number;
  sentiment?: string;
  financials?: {
    balance_sheet?: (string | number)[][];
    cash_flow?: (string | number)[][];
    income_statement?: (string | number)[][];
  };
};

export type Candle = {
  close: number;
  high: number;
  low: number;
  open: number;
  volume: number;
  dateTime: number;
  symbol: string;
};

export type TickerDetails = {
  sections: Section[];
  _id: string;
};

export function compareValues(a: number, b: number, c: number) {
  if (a < b && b < c) {
    return 'up';
  } else if (a > b && b > c) {
    return 'down';
  } else if (a === b && b === c) {
    return 'stable';
  } else if (a < b && b > c) {
    return 'fluctuating_down';
  } else if (a > b && b < c) {
    return 'fluctuating_up';
  } else {
    return 'unknown';
  }
}

export function parseFinancials(data: { [key: string]: Financials }, key: string): (string | number)[][] {
  const order = {
    'Balance Sheet': [
      'totalAssets',
      'currentAssets',
      'totalNonCurrentAssets',
      'totalLiabilitiesNetMinorityInterest',
      'currentLiabilities',
      'otherNonCurrentLiabilities',
      'totalEquityGrossMinorityInterest',
      'stockHoldersEquity',
      'totalCapitalization',
      'commonStockEquity',
      'netTangibleAssets',
      'workingCapital',
      'investedCapital',
      'tangibleBookValue',
      'totalDebt',
      'netDebt',
      'shareIssued',
      'ordinarySharesNumber',
    ],
    'Cash Flow': [
      'cashFlowFromContinuingOperatingActivities',
      'netIncomeFromContinuingOperations',
      'depreciationAndAmortization',
      'deferredTax',
      'deferredIncomeTax',
      'stockBasedCompensation',
      'otherNonCashItems',
      'changeInWorkingCapital',
      'changeInReceivables',
      'changesInAccountReceivables',
      'changeInInventory',
      'changeInPayablesAndAccruedExpense',
      'changeInPayable',
      'changeInAccountPayable',
      'changeInOtherCurrentAssets',
      'changeInOtherCurrentLiabilities',
      'changeInOtherWorkingCapital',
      'cashFlowFromContinuingInvestingActivities',
      'netPpePurchaseAndSale',
      'netBusinessPurchaseAndSale',
      'purchaseOfBusiness',
      'netInvestmentPurchaseAndSale',
      'purchaseOfInvestment',
      'saleOfInvestment',
      'netOtherInvestingChanges',
      'financingCashFlow',
      'cashFlowFromContinuingFinancingActivities',
      'netIssuancePaymentsOfDebt',
      'netLongTermDebtIssuance',
      'longTermDebtIssuance',
      'longTermDebtPayments',
      'netShortTermDebtIssuance',
      'netCommonStockIssuance',
      'commonStockIssuance',
      'commonStockPayments',
      'cashDividendsPaid',
      'netOtherFinancingCharges',
      'endCashPosition',
      'changesInCash',
      'beginningCashPosition',
      'capitalExpenditure',
      'issuanceOfCapitalStock',
      'issuanceOfDebt',
      'repaymentOfDebt',
      'repurchaseOfCapitalStock',
      'freeCashFlow',
    ],
    'Income Statement': [
      'totalRevenue',
      'operatingRevenue',
      'costOfRevenue',
      'grossProfit',
      'operatingExpense',
      'sellingGeneralAndAdministration',
      'researchAndDevelopment',
      'operatingIncome',
      'netNonOperatingInterestIncomeExpense',
      'otherIncomeExpense',
      'otherNonOperatingIncomeExpenses',
      'pretaxIncome',
      'taxProvision',
      'netIncome',
      'netIncomeCommonStockholders',
      'totalOperatingIncomeAsReported',
      'totalExpenses',
      'netIncomeFromContinuingAndDiscontinuedOperation',
      'normalizedIncome',
      'interestIncome',
      'interestExpense',
      'netInterestIncome',
      'ebit',
      'ebitda',
      'reconciledCostOfRevenue',
      'reconciledDepreciation',
      'normalizedEbitda',
      'taxRateForCalcs',
      'taxEffectOfUnusualItems',
    ],
  };
  const years = Object.keys(data).sort(); // Get the years in ascending order
  const lineItemsOrder = order[key];

  const tableData: (string | number)[][] = lineItemsOrder.map((lineItem: string) => {
    const row: (string | number)[] = [];
    row.push(getTitleForLineItem(lineItem));

    for (const year of years) {
      const value = data[year][key][lineItem];
      row.push(value);
    }

    return row;
  });

  return tableData;
}

function getTitleForLineItem(lineItem: string) {
  const titleMap = {
    beginningCashPosition: 'Beginning Cash Position',
    capitalExpenditure: 'Capital Expenditure',
    cashDividendsPaid: 'Cash Dividends Paid',
    cashFlowFromContinuingFinancingActivities: 'Cash Flow from Continuing Financing Activities',
    cashFlowFromContinuingInvestingActivities: 'Cash Flow from Continuing Investing Activities',
    cashFlowFromContinuingOperatingActivities: 'Cash Flow from Continuing Operating Activities',
    changeInAccountPayable: 'Change in Account Payable',
    changeInInventory: 'Change in Inventory',
    changeInOtherCurrentAssets: 'Change in Other Current Assets',
    changeInOtherCurrentLiabilities: 'Change in Other Current Liabilities',
    changeInOtherWorkingCapital: 'Change in Other Working Capital',
    changeInPayable: 'Change in Payable',
    changeInPayablesAndAccruedExpense: 'Change in Payables and Accrued Expense',
    changeInReceivables: 'Change in Receivables',
    changeInWorkingCapital: 'Change in Working Capital',
    changesInAccountReceivables: 'Changes in Account Receivables',
    changesInCash: 'Changes in Cash',
    commonStockEquity: 'Common Stock Equity',
    commonStockIssuance: 'Common Stock Issuance',
    commonStockPayments: 'Common Stock Payments',
    costOfRevenue: 'Cost Of Revenue',
    currentAssets: 'Current Assets',
    currentLiabilities: 'Current Liabilities',
    deferredIncomeTax: 'Deferred Income Tax',
    deferredTax: 'Deferred Tax',
    depreciationAndAmortization: 'Depreciation and Amortization',
    ebit: 'EBIT',
    ebitda: 'EBITDA',
    endCashPosition: 'End Cash Position',
    financingCashFlow: 'Financing Cash Flow',
    freeCashFlow: 'Free Cash Flow',
    grossProfit: 'Gross Profit',
    interestExpense: 'Interest Expense',
    interestIncome: 'Interest Income',
    investedCapital: 'Invested Capital',
    investingCashFlow: 'Investing Cash Flow',
    issuanceOfCapitalStock: 'Issuance of Capital Stock',
    issuanceOfDebt: 'Issuance of Debt',
    longTermDebtIssuance: 'Long-Term Debt Issuance',
    longTermDebtPayments: 'Long-Term Debt Payments',
    netBusinessPurchaseAndSale: 'Net Business Purchase and Sale',
    netCommonStockIssuance: 'Net Common Stock Issuance',
    netDebt: 'Net Debt',
    netIncome: 'Net Income',
    netIncomeCommonStockholders: 'Net Income Common Stockholders',
    netIncomeFromContinuingAndDiscontinuedOperation: 'Net Income From Continuing And Discontinued Operation',
    netIncomeFromContinuingOperationNetMinorityInterest: 'Net Income From Continuing Operation Net Minority Interest',
    netIncomeFromContinuingOperations: 'Net Income from Continuing Operations',
    netInterestIncome: 'Net Interest Income',
    netInvestmentPurchaseAndSale: 'Net Investment Purchase and Sale',
    netIssuancePaymentsOfDebt: 'Net Issuance Payments of Debt',
    netLongTermDebtIssuance: 'Net Long-Term Debt Issuance',
    netNonOperatingInterestIncomeExpense: 'Net Non-Operating Interest Income Expense',
    netOtherFinancingCharges: 'Net Other Financing Charges',
    netOtherInvestingChanges: 'Net Other Investing Changes',
    netPpePurchaseAndSale: 'Net PPE Purchase and Sale',
    netShortTermDebtIssuance: 'Net Short-Term Debt Issuance',
    netTangibleAssets: 'Net Tangible Assets',
    normalizedEbitda: 'Normalized EBITDA',
    normalizedIncome: 'Normalized Income',
    operatingCashFlow: 'Operating Cash Flow',
    operatingExpense: 'Operating Expense',
    operatingIncome: 'Operating Income',
    operatingRevenue: 'Operating Revenue',
    ordinarySharesNumber: 'Ordinary Shares Number',
    otherIncomeExpense: 'Other Income Expense',
    otherNonCashItems: 'Other Non-Cash Items',
    otherNonCurrentLiabilities: 'Other Non-Current Liabilities',
    otherNonOperatingIncomeExpenses: 'Other Non-Operating Income Expenses',
    pretaxIncome: 'Pre-Tax Income',
    purchaseOfBusiness: 'Purchase of Business',
    purchaseOfInvestment: 'Purchase of Investment',
    reconciledCostOfRevenue: 'Reconciled Cost Of Revenue',
    reconciledDepreciation: 'Reconciled Depreciation',
    repaymentOfDebt: 'Repayment of Debt',
    repurchaseOfCapitalStock: 'Repurchase of Capital Stock',
    researchAndDevelopment: 'Research and Development',
    saleOfInvestment: 'Sale of Investment',
    sellingGeneralAndAdministration: 'Selling General And Administration',
    shareIssued: 'Share Issued',
    stockBasedCompensation: 'Stock-Based Compensation',
    stockHoldersEquity: 'Stockholders Equity',
    tangibleBookValue: 'Tangible Book Value',
    taxEffectOfUnusualItems: 'Tax Effect Of Unusual Items',
    taxProvision: 'Tax Provision',
    taxRateForCalcs: 'Tax Rate For Calcs',
    totalAssets: 'Total Assets',
    totalCapitalization: 'Total Capitalization',
    totalDebt: 'Total Debt',
    totalEquityGrossMinorityInterest: 'Total Equity Gross Minority Interest',
    totalExpenses: 'Total Expenses',
    totalLiabilitiesNetMinorityInterest: 'Total Liabilities Net Minority Interest',
    totalNonCurrentAssets: 'Total Non-Current Assets',
    totalOperatingIncomeAsReported: 'Total Operating Income As Reported',
    totalRevenue: 'Total Revenue',
    workingCapital: 'Working Capital',
  };

  return titleMap[lineItem] || lineItem;
}

export function splitDescription(description: string) {
  const descriptionParts = description.split('. ');
  let blurb = descriptionParts.length > 1 ? descriptionParts.shift() + '.' : descriptionParts.join('. ');
  if (blurb.length < 100 && descriptionParts.length > 1) {
    blurb += ' ' + descriptionParts.shift() + '.';
  }
  const remainingDescription = descriptionParts.length > 0 ? descriptionParts.join('. ') : '';
  return [blurb, remainingDescription];
}

export function calculatePercentChange(previousValue, currentValue) {
  const change = currentValue - previousValue;
  const percentChange = (change / previousValue) * 100;
  return percentChange;
}

export function mapPeerInfo(peer: Peer, rankings: StockReportRankings[] = []): MappedPeer {
  return {
    exchange: peer.exchange,
    name: peer.name,
    options: {
      graphic: [
        {
          bottom: '25%',
          right: '40%',
          style: {
            image: '/next-assets/images/Benzinga-logo-navy.svg',
            opacity: 0.25,
            width: 60,
          },
          type: 'image',
        },
      ],
      grid: {
        bottom: '0%',
        containLabel: true,
        left: '0%',
        right: '0%',
        top: '5%',
      },
      radar: {
        indicator: [
          { max: 5, name: 'V' },
          { max: 5, name: 'G' },
          { max: 5, name: 'H' },
          { max: 5, name: 'P' },
          { max: 5, name: 'D' },
        ],
        shape: 'circle',
      },
      series: [
        {
          data: [
            {
              areaStyle: {
                opacity: 0.1,
              },
              itemStyle: {
                color: 'rgb(16, 96, 255)',
              },
              label: {
                show: false,
              },
              value: [
                peer.scores.value,
                peer.scores.growth,
                peer.scores.health,
                peer.scores.past,
                peer.scores.dividend,
              ],
            },
          ],
          type: 'radar',
        },
      ],
    },
    symbol: peer.symbol,
    rankings: rankings.find(ranking => ranking.symbol === peer.symbol) || null,
  };
}

export function generateChartOption(candles: Candle[], chartColor: string[]): EChartsOption {
  return {
    grid: {
      bottom: '3%',
      containLabel: true,
      left: '2%',
      right: '4%',
      top: '12%',
    },
    series: [
      {
        areaStyle: {
          color: {
            colorStops: [
              { color: chartColor[0], offset: 0 },
              { color: chartColor[1], offset: 1 },
            ],
            global: false,
            type: 'linear',
            x: 0,
            x2: 0,
            y: 0,
            y2: 1,
          },
        },
        data: candles.map((candle: Candle) => candle.close),
        lineStyle: {
          color: chartColor[2],
          width: 2,
        },
        symbol: 'none',
        type: 'line',
      },
    ],
    xAxis: {
      axisLabel: {
        show: false,
      },
      axisLine: {
        onZero: false,
        show: false,
      },
      axisTick: {
        show: false,
      },
      boundaryGap: false,
      data: candles.map((candle: Candle) => dayjs(candle.dateTime).format('MMM D')),
      min: 'dataMin',
      splitLine: {
        show: false,
      },
      type: 'category',
    },
    yAxis: {
      axisLabel: {
        show: false,
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      boundaryGap: false,
      min: 'dataMin',
      splitLine: {
        show: false,
      },
      // type: 'category',
    },
  };
}

export function getCandle(candles: Candle[], index: number) {
  return candles[index];
}

export function getCandleColor(prevCandle: Candle, currentCandle: Candle) {
  if (currentCandle?.close >= prevCandle?.close) {
    return ['rgba(13, 233, 112, 0.3)', 'rgba(13, 233, 112, 0)', 'rgb(13, 233, 112)'];
  } else {
    return ['rgba(233, 42, 13, 0.3)', 'rgba(233, 42, 13, 0)', 'rgb(233, 42, 13)'];
  }
}

export function addSection(tickerDetails: TickerDetails, section: Section) {
  if (!section) {
    return tickerDetails;
  }

  return {
    ...tickerDetails,
    sections: [...tickerDetails.sections, section],
  };
}
